import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiService } from '@/services/api';

interface DigiKeyAuthStatus {
  authenticated: boolean;
  expires_at?: string;
  updated_by?: string;
}

interface DigiKeyAuthProps {
  onAuthChange?: (authenticated: boolean) => void;
}

export const DigiKeyAuth: React.FC<DigiKeyAuthProps> = ({ onAuthChange }) => {
  const { t } = useTranslation('bom');
  const [status, setStatus] = useState<DigiKeyAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const authStatus = await ApiService.getDigiKeyAuthStatus();
      setStatus(authStatus);
      onAuthChange?.(authStatus.authenticated);
    } catch (err: any) {
      setError(err.message);
      setStatus({ authenticated: false });
      onAuthChange?.(false);
    } finally {
      setLoading(false);
    }
  }, [onAuthChange]);

  useEffect(() => {
    fetchStatus();

    // Listen for OAuth callback messages from popup window
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'digikey-auth-success') {
        fetchStatus();
      } else if (event.data?.type === 'digikey-auth-error') {
        setError(event.data.error || 'Authentication failed');
        setActionLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const { auth_url } = await ApiService.initiateDigiKeyAuth();

      // Open DigiKey OAuth in a popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        auth_url,
        'DigiKey OAuth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // Poll for popup closure (in case postMessage doesn't work)
      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          setActionLoading(false);
          // Refresh status after popup closes
          setTimeout(() => fetchStatus(), 500);
        }
      }, 500);

      // Set a timeout to stop waiting
      setTimeout(() => {
        clearInterval(pollTimer);
        setActionLoading(false);
      }, 5 * 60 * 1000); // 5 minutes

    } catch (err: any) {
      setError(err.message);
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setActionLoading(true);
      setError(null);

      await ApiService.revokeDigiKeyAuth();
      setStatus({ authenticated: false });
      onAuthChange?.(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatExpiry = (expiresAt: string) => {
    const date = new Date(expiresAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs <= 0) {
      return t('digikey.expired', 'Expired');
    }

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) {
      return t('digikey.expiresInMinutes', 'Expires in {{minutes}} min', { minutes: diffMins });
    }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      return t('digikey.expiresInHours', 'Expires in {{hours}} hours', { hours: diffHours });
    }

    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        {t('digikey.checkingStatus', 'Checking DigiKey status...')}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {/* Status indicator */}
      <div className="flex items-center gap-2">
        <div
          className={`w-2 h-2 rounded-full ${
            status?.authenticated ? 'bg-green-500' : 'bg-gray-400'
          }`}
        />
        <span className="text-sm font-medium">
          {t('digikey.title', 'DigiKey API')}
        </span>
      </div>

      {/* Status details */}
      {status?.authenticated ? (
        <div className="flex items-center gap-3">
          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
            {t('digikey.connected', 'Connected')}
          </span>
          {status.expires_at && (
            <span className="text-xs text-gray-500">
              {formatExpiry(status.expires_at)}
            </span>
          )}
          <button
            onClick={handleDisconnect}
            disabled={actionLoading}
            className="text-xs text-red-600 hover:text-red-700 hover:underline disabled:opacity-50"
          >
            {actionLoading ? t('digikey.disconnecting', 'Disconnecting...') : t('digikey.disconnect', 'Disconnect')}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {t('digikey.notConnected', 'Not connected')}
          </span>
          <button
            onClick={handleConnect}
            disabled={actionLoading}
            className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50 flex items-center gap-1"
          >
            {actionLoading ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('digikey.connecting', 'Connecting...')}
              </>
            ) : (
              t('digikey.connect', 'Connect')
            )}
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <span className="text-xs text-red-500">{error}</span>
      )}
    </div>
  );
};

export default DigiKeyAuth;
